"use client";

import { useEffect, useRef, type ReactNode } from "react";
import Image, { getImageProps } from "next/image";
import { PortableText } from "@portabletext/react";
import { stegaClean } from "@sanity/client/stega";
import { useInView } from "framer-motion";
import imageUrlBuilder from "@sanity/image-url";
import Chip from "@/components/global/ui/Chip";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { cn } from "@/lib/utils";
import type { AiMedia, AiSection } from "@/sanity/pages/PageAiFeaturesTemplate";
import styles from "./ai-features.module.scss";

export function DeferredPaint({ className }: { className: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const near = useInView(ref, { once: true, margin: "600px" });
  return (
    <span
      ref={ref}
      className={className}
      data-texture-ready={near}
      aria-hidden="true"
    />
  );
}

export function Reveal({
  children,
  className,
  variant = "image",
}: {
  children: ReactNode;
  className?: string;
  variant?: "text" | "image";
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node || window.matchMedia("(prefers-reduced-motion: reduce)").matches)
      return;
    let animations: Animation[] = [];
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        const targets = variant === "text" ? [...node.children] : [node];
        animations = targets.map((target, i) =>
          target.animate(
            [
              {
                opacity: 0,
                transform: "translateY(16px)",
              },
              { opacity: 1, transform: "translateY(0)" },
            ],
            {
              duration: 700,
              delay: variant === "text" ? Math.min(i * 80, 240) : 0,
              fill: "backwards",
              easing: "cubic-bezier(0.16, 1, 0.3, 1)",
            },
          ),
        );
        observer.disconnect();
      },
      { threshold: 0, rootMargin: "0px 0px -10% 0px" },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
      animations.forEach((animation) => animation.cancel());
    };
  }, [variant]);
  return (
    <div ref={ref} className={className} data-ai-reveal>
      {children}
    </div>
  );
}

export function SectionHeading({
  data,
  align = "center",
}: {
  data?: AiSection;
  align?: "center" | "left";
}) {
  if (!data) return null;
  return (
    <Reveal
      variant="text"
      className={cn(
        "mx-auto flex flex-col gap-[2.5rem]",
        align === "center"
          ? "max-w-[120rem] items-center text-center"
          : "items-start text-left",
      )}
    >
      {data.label && <Chip text={data.label} className={styles.chip} />}
      {data.title && <h2 className={styles.heading}>{data.title}</h2>}
      {data.description?.length ? (
        <div className={styles.description}>
          <PortableText
            value={data.description}
            components={richTextComponent}
          />
        </div>
      ) : null}
      {(data.cta?.cta || (data.cta?.type && data.cta.shape)) && (
        <div className={styles.sectionHeadingCTA}>
          <CTAButton data={data.cta} showIncomplete />
        </div>
      )}
    </Reveal>
  );
}

export function ProductImage({
  image,
  mobileImage,
  priority = false,
  sizes,
  className,
}: {
  image?: AiMedia;
  mobileImage?: AiMedia;
  priority?: boolean;
  sizes: string;
  className?: string;
}) {
  if (!image?.src) return null;
  const source = (media: AiMedia) => {
    const clean = stegaClean(media);
    if (!clean.crop || !clean.src.startsWith("https://cdn.sanity.io/images/"))
      return { src: clean.src, width: clean.width, height: clean.height };
    const [, , projectId, dataset] = new URL(clean.src).pathname.split("/");
    const width = Math.round(
      clean.width * (1 - clean.crop.left - clean.crop.right),
    );
    const height = Math.round(
      clean.height * (1 - clean.crop.top - clean.crop.bottom),
    );
    const src = imageUrlBuilder({ projectId, dataset })
      .image({
        asset: { _ref: "", url: clean.src },
        crop: clean.crop,
        hotspot: clean.hotspot,
      })
      .width(width)
      .height(height)
      .url();
    return { src, width, height };
  };
  const props = {
    ...source(image),
    alt: image.alt ?? "",
    sizes,
    quality: 100,
    priority,
  };
  const mobileProps = mobileImage?.src
    ? getImageProps({ ...props, ...source(mobileImage) }).props
    : undefined;
  return (
    <picture className="block w-full">
      {mobileProps && (
        <source
          media="(max-width: 1023px)"
          srcSet={mobileProps.srcSet}
          sizes={mobileProps.sizes}
          width={mobileProps.width}
          height={mobileProps.height}
        />
      )}
      {/* A desktop preload would fetch the wrong asset when picture selects mobile. */}
      <Image
        {...props}
        priority={priority && !mobileProps}
        loading={priority ? "eager" : undefined}
        fetchPriority={priority ? "high" : undefined}
        className={cn("h-auto w-full", className)}
      />
    </picture>
  );
}
